/**
 * Fire Protection Information Cards
 * Extracted from EnginePropulsionSection to maintain file size limits
 */
import React from 'react';

const FireProtectionInformation = ({ 
  showChoice,
  isOutboard, 
  isPureBatteryElectric,
  isInboardBelowDeck,
  currentPropulsionType,
  currentEngineLocation,
  vesselConfig,
  updateConfig 
}) => {

  // Extract propulsion details from unified configuration if available
  const getEffectivePropulsionType = () => {
    if (vesselConfig.propulsionConfiguration) {
      const config = vesselConfig.propulsionConfiguration;
      if (config.includes('battery') && !config.includes('hybrid')) return 'battery_electric';
      if (config.includes('hybrid')) return 'battery_hybrid';
      if (config.includes('petrol')) return 'petrol';
      if (config.includes('diesel')) return 'diesel';
    }
    return currentPropulsionType || 'diesel';
  };

  const getEffectiveEngineLocation = () => {
    if (vesselConfig.propulsionConfiguration) {
      const config = vesselConfig.propulsionConfiguration;
      if (config.includes('above')) return 'above_deck';
      if (config.includes('below')) return 'below_deck';
      if (config.includes('outboard')) return 'outboard';
    }
    return currentEngineLocation || 'above_deck';
  };

  const effectivePropulsionType = getEffectivePropulsionType();
  const effectiveEngineLocation = getEffectiveEngineLocation();
  const isEffectiveOutboard = effectiveEngineLocation === 'outboard' || isOutboard;
  const isEffectiveBatteryElectric = effectivePropulsionType === 'battery_electric' || isPureBatteryElectric;
  const isEffectiveInboardBelowDeck = effectiveEngineLocation === 'below_deck' || isInboardBelowDeck;

  if (showChoice) {
    // Fire Protection Choice - for above deck inboard conventional engines
    return (
      <div className="sm:col-span-2">
        <label className="block text-sm font-medium text-gray-900 mb-2">
          Fire Protection Method for Above Deck {effectivePropulsionType === 'battery_hybrid' ? 'Diesel Engine' : 'Engine'} (WBC3 Table 16.1.1.1)
          {effectivePropulsionType === 'battery_hybrid' && (
            <span className="block text-xs text-amber-600 mt-1 font-normal">
              Note: Battery components require separate fixed fire suppression (WBC3 Annex 1)
            </span>
          )}
        </label>
        <div className="space-y-2">
          <label className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={vesselConfig.engineBoxProtection?.firePort || false}
              onChange={(e) => {
                const isChecked = e.target.checked;
                updateConfig({ 
                  engineBoxProtection: {
                    ...vesselConfig.engineBoxProtection,
                    firePort: isChecked,
                    fixedSystem: isChecked ? false : (vesselConfig.engineBoxProtection?.fixedSystem || false)
                  }
                });
              }}
              className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-600"
            />
            <span className="text-sm text-gray-700">Portable extinguishers to fire port</span>
          </label>
          <label className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={vesselConfig.engineBoxProtection?.fixedSystem || false}
              onChange={(e) => {
                const isChecked = e.target.checked;
                updateConfig({ 
                  engineBoxProtection: {
                    ...vesselConfig.engineBoxProtection,
                    fixedSystem: isChecked,
                    firePort: isChecked ? false : (vesselConfig.engineBoxProtection?.firePort || false)
                  },
                  // Also update fixedFFE.engine when selecting fixed system
                  fixedFFE: {
                    ...vesselConfig.fixedFFE,
                    engine: isChecked
                  }
                });
              }}
              className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-600"
            />
            <span className="text-sm text-gray-700">Fixed fire extinguishing system</span>
          </label>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Select ONE method for engine box fire protection (required per WBC3)
        </p>
      </div>
    );
  }

  if (!isEffectiveOutboard) {
    // Fire Protection Information - for cases where no choice is available
    return (
      <div className="sm:col-span-2">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <h4 className="text-sm font-medium text-blue-900 mb-1">
            Fire Protection Requirements
          </h4>
          <div className="text-xs text-blue-700">
            {isEffectiveBatteryElectric ? (
              <>
                <p>• <strong>MANDATORY:</strong> Fixed fire suppression system (WBC3 Annex 1)</p>
                <p>• Battery systems have no choice - fixed system required</p>
              </>
            ) : isEffectiveInboardBelowDeck ? (
              <>
                <p>• <strong>MANDATORY:</strong> Fixed fire extinguishing system</p>
                <p>• Below deck {effectivePropulsionType === 'battery_hybrid' ? 'diesel engines' : 'engines'} require fixed system per WBC3</p>
                {effectivePropulsionType === 'battery_hybrid' && (
                  <p>• <strong>Note:</strong> This covers the diesel engine component only</p>
                )}
              </>
            ) : null}
            {effectivePropulsionType === 'battery_hybrid' && (
              <p>• <strong>Additional:</strong> Battery components also require fixed suppression</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Outboard Fire Protection Information
  return (
    <div className="sm:col-span-2">
      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
        <h4 className="text-sm font-medium text-green-900 mb-1">
          Fire Protection Requirements
        </h4>
        <div className="text-xs text-green-700">
          <p>• <strong>AUTOMATIC:</strong> Portable fire extinguishers only</p>
          <p>• All outboard engines use portable extinguishers per WBC3 Table 16.1.1.1</p>
          {effectivePropulsionType === 'battery_hybrid' && (
            <p>• <strong>Additional:</strong> Battery components require fixed suppression (WBC3 Annex 1)</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default FireProtectionInformation;